import { NextPage } from 'next';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import handleApiErrors from 'src/common/utils/handleApiErrors';
import serverGetListing, { isListingRedirect } from 'src/common/utils/serverGetListing';
import serverGetUser from 'src/common/utils/serverGetUser';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import prependLocale from 'src/common/utils/prependLocale';
import { isHashedId } from 'src/common/utils/validators';
import BookNowDirectRoot from 'src/book-now-direct/components/BookNowDirectRoot';
import serverGetInstabookDates from 'src/trip-cart-deprecated/utils/serverGetInstabookDates';
import { addMonths, startOfMonth } from 'date-fns';
import { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import type { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import serverGetInstabooks from 'src/common/utils/serverGetInstabooks';
import {
  DEPARTURE_DATE_FIELD,
  ADULTS,
  MESSAGE_TO_OWNER,
  OCCASION,
  PARAM_KEYS,
  type BookNowDirectFormState,
  type SessionStorageData,
  type BookNowDirectFormStateSerialized,
} from 'src/book-now-direct/constants';
import BookNowDirectProvider from 'src/book-now-direct/providers/BookNowDirectProvider';
import ensureString from 'src/common/utils/ensureString';
import { useEffect, useState } from 'react';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { filterInstabooks, parseSerializedFormState } from 'src/book-now-direct/helpers';
import { transformUrlParamValuesToProps } from 'src/book-now-direct/parseAndValidateUrlParams';
import serverGetOccasions from 'src/common/utils/serverGetOccasions';
import type { Occasion } from 'src/common/types/api/v4/trips/occasions';
import MetaFields from 'src/book-now-direct/components/MetaFields';
import { BlockedTime, BookingMotivatorsProvider, BookingMotivatorsState } from 'src/common/providers/useBookingMotivators';
import serverGetBookingMotivators from 'src/common/utils/serverGetBookingMotivators';
import serverGetBlockedTimes from 'src/common/utils/serverGetBlockedTimes';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';

export type BookNowDirectServerProps = {
  listing: FullBoatSearchDetail;
  initialCapacity: number;
  /** First 2 months of instabooks, format `['2025-03-27']` */
  initialInstabookDates: string[];
  initialInstabooksOnDate: InstabookAvailability[];
  /** Optional key to sessionStorage, returned by server to avoid client router work  */
  sessionKey?: string;
  /** When owners link from their websites, they will provide initial values as url params */
  initializeFromUrlParams?: BookNowDirectFormStateSerialized | null;
  occasions: Occasion[];
  bookingMotivators: BookingMotivatorsState;
  blockedTimes: BlockedTime[];
};

/**
 * A minimally branded book-now form that owners share with direct-booking renters or guests.
 * The page url is shared by owner from `/admin/direct/`
 * The submitted instabook POST payload must contain key `is_direct_booking: true`
 */
const BookNowDirect: NextPage<BookNowDirectServerProps> = ({
  initialCapacity,
  listing,
  initialInstabookDates,
  initialInstabooksOnDate,
  sessionKey,
  initializeFromUrlParams,
  occasions,
  bookingMotivators,
  blockedTimes,
}) => {
  const [initialFormData, setInitialFormData] = useState<BookNowDirectFormState | undefined>(
    initializeFromUrlParams ? parseSerializedFormState(initializeFromUrlParams) : undefined,
  );
  const [t] = useTranslation('book-now-direct');

  useEffect(() => {
    if (initializeFromUrlParams) {
      // state is initialized with url params if provided by server
      return;
    }
    // Beware: Instabooks are volatile. The previously selected instabook may be unavailable.
    if (!sessionKey) { return; }
    const sessionData = sessionStore.getItem(sessionKey);
    if (!sessionData) { return; }
    const { formData } = JSON.parse(sessionData) as SessionStorageData;

    const formDataParsed = parseSerializedFormState(formData);

    /** Does the day from sessionStorage exist in initialInstabookDates?. If true returns as date */
    const dateIfAvailable = (
      formData[DEPARTURE_DATE_FIELD] && initialInstabookDates.includes(formData[DEPARTURE_DATE_FIELD]?.split('T')[0])
    ) ? formDataParsed[DEPARTURE_DATE_FIELD] : undefined;

    /** All form values exist- it should result in a single instabook without selecting cheapest  */
    const isInstabookIsStillAvailable = filterInstabooks(initialInstabooksOnDate, formDataParsed);
    if (isInstabookIsStillAvailable?.length === 1) {
      setInitialFormData({
        ...formDataParsed,
        [DEPARTURE_DATE_FIELD]: dateIfAvailable,
      });
    } else {
      // If instabook is no longer available, set partial, ask user to re-select
      setInitialFormData({
        [ADULTS]: formDataParsed[ADULTS],
        [MESSAGE_TO_OWNER]: formDataParsed[MESSAGE_TO_OWNER],
        [OCCASION]: formDataParsed[OCCASION],
        [DEPARTURE_DATE_FIELD]: dateIfAvailable,
      });
    }

    sessionStore.removeItem(sessionKey);
  }, [initialInstabookDates, initialInstabooksOnDate, initializeFromUrlParams, sessionKey]);

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <MetaFields
        listing={listing}
      />
      <BookingMotivatorsProvider
        boatId={listing.id}
        initialInstabookDates={initialInstabookDates}
        bookingMotivators={bookingMotivators}
      >
        <BookNowDirectProvider
          listing={listing}
          initialInstabookDates={initialInstabookDates}
          initialInstabooksOnDate={initialInstabooksOnDate}
          key={initialInstabooksOnDate[0]?.transaction.amounts.renter_currency.code}
          blockedTimes={blockedTimes}
        >
          <BookNowDirectRoot
            initialCapacity={initialCapacity}
            listing={listing}
            // change in props will re-init form with new values from sessionStorage
            initialFormData={initialFormData}
            occasions={occasions}
          />
        </BookNowDirectProvider>
      </BookingMotivatorsProvider>
    </>
  );
};

type Params = {
  listing_id: string;
};
type Query = {
  session_key?: string;
};

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<BookNowDirectServerProps, Params, Query>(
    async (context) => {
      const listingId = ensureString(context.params?.listing_id);
      // return a 404 response early if the id doesn't pass basic validation.
      if (!isHashedId(listingId)) {
        return {
          notFound: true,
        };
      }

      const translations = loadTranslations(
        context.locale,
        [
          'common',
          'navbar',
          'book-now',
          'destination-navigation',
          'search-and-filters',
          'boat-categories',
          'listing-page',
          'book-now-direct',
          'footer-white-labeled',
          'admin-listing-preview',
          'price-placeholders',
        ],
      );

      const startMonthDate = startOfMonth(todayDate());
      const thisMonthAndNext = [
        formatDate(startMonthDate),
        formatDate(addMonths(startMonthDate, 1)),
      ];

      const [
        user,
        listing,
        initialInstabookDates,
        occasions,
        bookingMotivators,
        blockedTimes,
      ] = await Promise.all([
        serverGetUser(context),
        serverGetListing(context),
        serverGetInstabookDates(context, thisMonthAndNext, true),
        serverGetOccasions(context),
        serverGetBookingMotivators(context, listingId, thisMonthAndNext[0]),
        serverGetBlockedTimes(context, listingId, thisMonthAndNext[0]),
      ]);

      const initialInstabookDatesSorted = initialInstabookDates.sort() ?? [];

      if (isListingRedirect(listing)) {
        return {
          redirect: {
            statusCode: listing.code,
            destination: prependLocale(listing.redirect_url, context.locale),
          },
        };
      }

      if (!listing?.id) {
        return {
          notFound: true,
        };
      }

      const initialCapacity = Number(listing.capacity);

      /** optional session_key to re-init form values between redirects */
      const sessionKey = ensureString(context.query[PARAM_KEYS.SESSION_KEY]);

      /**
       * Owners can optionally provide initial values via url params.
       * e.g. `?date=2025-03-27&duration=8&time=11:00:00&guests=2&captained=true`
       * Before returning as props, we validate and transform to our desired Form State shape
       */
      const initialValuesFromUrl = transformUrlParamValuesToProps(
        context.query,
        initialInstabookDates,
      );

      let initialInstabooksOnDate: InstabookAvailability[] = [];
      if (initialInstabookDatesSorted.length) {
        initialInstabooksOnDate = await serverGetInstabooks(
          context,
          listing.id,
          // if date was provided via url param, ensure initial instabooks use that date
          initialValuesFromUrl?.[DEPARTURE_DATE_FIELD] || initialInstabookDatesSorted[0],
          initialCapacity,
          true, // Pricing changes for Direct Instabooks
        );
      }

      const motivators = { ...listing.booking_motivators };
      bookingMotivators.forEach((motivator) => {
        motivators[motivator.date] = motivator.motivators;
      });

      return {
        props: {
          user,
          listing: listing as FullBoatSearchDetail,
          initialCapacity,
          initialInstabookDates: initialInstabookDatesSorted,
          initialInstabooksOnDate,
          sessionKey,
          initializeFromUrlParams: initialValuesFromUrl,
          occasions,
          bookingMotivators: motivators,
          blockedTimes,
          ...await translations,
        },
      };
    },
  ),
);

export default BookNowDirect;
